// File: frontend/src/components/admin/AdminAuditLogs.tsx

import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuditLogEntry,
  AuditAction,
  AuditTargetType,
  AuditLogFilters,
  getAuditLogs,
} from "../../services/adminAuditLogService";
import { Pagination } from "../../utils/Pagination";
import { ADMIN_PAGE_SIZE } from "../../constants";
import { formatAdminDate } from "../../utils/adminUtils";
import { generateProfileUrl } from "../../utils/slugGenerator";
import "./AdminAuditLogs.css";

interface AdminAuditLogsProps {
  isAdmin: boolean;
}

// All available action filter options
// Sport type enum values that may appear inside targetLabel for TEAM lock/unlock actions.
// We replace them with translated display names so the audit log is fully localized.
const SPORT_TYPE_I18N: Record<string, string> = {
  FOOTBALL:         "menu_sport_football",
  BASKETBALL:       "menu_sport_basketball",
  VOLLEYBALL:       "menu_sport_volleyball",
  HANDBALL:         "menu_sport_handball",
  ICE_HOCKEY:       "menu_sport_ice_hockey",
  BASEBALL:         "menu_sport_baseball",
  AMERICAN_FOOTBALL:"menu_sport_american_football",
};

const ACTION_OPTIONS: AuditAction[] = [
  "DELETE_ENTRY", "RESTORE_ENTRY", "EDIT_ENTRY", "HARD_DELETE_ENTRY",
  "CREATE_USER", "DELETE_USER", "HARD_DELETE_USER", "BAN_USER", "UNBAN_USER",
  "SUSPEND_USER", "UNSUSPEND_USER", "CHANGE_USER_ROLE", "CHANGE_USER_STATUS",
  "CHANGE_USER_PASSWORD", "CHANGE_USERNAME", "CHANGE_USER_EMAIL", "SET_VERIFIED",
  "EDIT_BIO", "DELETE_BIO", "BLOCK_BIO", "UNBLOCK_BIO",
  "BAN_IP", "UNBAN_IP", "DELETE_IP_BAN",
  "LOCK_TOPIC", "UNLOCK_TOPIC",
  "MERGE_TOPIC", "MOVE_ENTRY",
  "HARD_DELETE_TOPIC",
];

const TARGET_OPTIONS: AuditTargetType[] = ["ENTRY", "USER", "IP", "MATCH", "CLUB", "TEAM", "PERSON", "CUSTOM_TOPIC"];

/**
 * Convert an AuditAction enum value to a CSS class suffix.
 * e.g. "DELETE_ENTRY" → "delete-entry"
 */
const actionToCssClass = (action: AuditAction): string =>
  action.toLowerCase().replace(/_/g, "-");

// Maps raw boolean detail field names (as written by the backend, e.g. "hardDelete: true")
// to the i18n key for their short label. Used by formatAuditDetail below.
const BOOLEAN_DETAIL_FIELD_I18N: Record<string, string> = {
  verified:       "audit_detail_verified",
  deleteEntries:  "audit_detail_delete_entries",
  hardDelete:     "audit_detail_hard_delete",
  hardDeleteUser: "audit_detail_hard_delete_user",
};

// Translates "role: X" / "status: X" fragments inside raw detail strings (e.g. "role: ADMIN")
// into localized text, reusing the same status_<value> i18n keys used for role/status badges
// elsewhere in the admin panel. Also translates raw boolean fields (e.g. "verified: true",
// "deleteEntries: true, hardDelete: true") into localized label + Evet/Hayır (Yes/No).
// Fragments that don't match a known key are left untouched.
const formatAuditDetail = (detail: string, t: (key: string) => string): string =>
  detail
    .replace(/\b(role|status)\s*:\s*([A-Z_]+)/g, (match, label: string, value: string) => {
      const i18nKey = `status_${value.toLowerCase()}`;
      const translatedValue = t(i18nKey);
      if (translatedValue === i18nKey) return match; // no translation available, keep original
      const translatedLabel = t(label === "role" ? "admin_col_role" : "admin_col_status");
      return `${translatedLabel}: ${translatedValue}`;
    })
    .replace(/\b(verified|deleteEntries|hardDelete|hardDeleteUser)\s*:\s*(true|false)/g, (match, field: string, value: string) => {
      const labelKey = BOOLEAN_DETAIL_FIELD_I18N[field];
      if (!labelKey) return match; // unknown field, keep original
      const translatedLabel = t(labelKey);
      const translatedValue = t(value === "true" ? "admin_yes_toggle" : "admin_no_toggle");
      return `${translatedLabel}: ${translatedValue}`;
    })
    // Translate the "reason:" label prefix, keeping the admin's free-text note as written
    .replace(/\breason\s*:/g, () => `${t("audit_detail_reason")}:`)
    // Suspension duration written by the backend as "<n> days" (e.g. "7 days — spam")
    .replace(/\b(\d+)\s*days\b/g, (_m, n: string) => `${n} ${t("audit_detail_days")}`)
    // MOVE_ENTRY detail: "entryIds=[708, 709]" → localized label
    .replace(/\bentryIds\s*=/g, () => `${t("audit_detail_entry_ids")}: `)
    // MERGE_TOPIC detail: "movedEntries=5, movedDrafts=2, sourceId=7" → localized labels
    .replace(/\bmovedEntries\s*=/g, () => `${t("audit_detail_moved_entries")}: `)
    .replace(/\bmovedDrafts\s*=/g, () => `${t("audit_detail_moved_drafts")}: `)
    .replace(/\bsourceId\s*=/g, () => `${t("audit_detail_source_id")}: `)
    // HARD_DELETE_TOPIC detail: "slug=..., deletedEntries=3" → localized label
    // (the slug itself stays raw — it is the URL the admin recognizes)
    .replace(/\bdeletedEntries\s*=/g, () => `${t("audit_detail_deleted_entries")}: `)
    // HARD_DELETE_ENTRY detail: "author:<username>" → localized label
    .replace(/\bauthor\s*:\s*/g, () => `${t("audit_detail_author")}: `)
    // Legacy fixed sentence written by an older backend version for
    // HARD_DELETE_USER rows — current code writes "hardDeleteUser: true"
    // instead, but rows persisted before that change still carry this text
    .replace(/user row physically removed/g, () => t("audit_detail_user_row_removed"));


// Replaces raw sport type enum values inside a target label (e.g. "(FOOTBALL)")
// with their translated display names — shared by the table cell and the modal.
const translateSportTypes = (label: string, t: (key: string) => string): string =>
  Object.entries(SPORT_TYPE_I18N).reduce(
    (acc, [sport, key]) => acc.replace(`(${sport})`, `(${t(key)})`),
    label
  );

// ── Target label linkification (detail modal) ─────────────────────────────
// Entry snippets stored in target_label carry the raw ref tokens of the entry
// content, e.g. "(takim: Türkiye futbol takımı||turkiye/futbol)". Instead of
// showing the raw token, render it as a link to the referenced page. Handled
// tokens (inline + "(bkz: ...)" variants share the same "||tail" semantics):
//   (kisi: name||slug)                → /kisi/{slug}
//   (kulup: name||slug)               → /kulup/{slug}
//   (takim: label||clubSlug/teamSlug) → /takim/{clubSlug}/{teamSlug}
//   (baslik: title||slug)             → /gundem/{slug}
//   (mac: label||id)                  → plain label text (match URLs need
//                                       team names + date, not just the id)
//   https://...                       → external link (new tab)
// Anything else is left as plain text.
const AUDIT_LABEL_TOKEN_PATTERN =
  /\((?:bkz-)?(kisi|kulup|takim|baslik|mac):\s*(.*?)\|\|([^)]+)\)|(https?:\/\/[^\s)]+)/g;

const linkifyAuditLabel = (label: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  const regex = new RegExp(AUDIT_LABEL_TOKEN_PATTERN);
  let match: RegExpExecArray | null;
  while ((match = regex.exec(label)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(label.slice(lastIndex, match.index));
    }
    const [, kind, refLabel, tail, url] = match;
    if (url) {
      // Raw URL — open externally in a new tab
      nodes.push(
        <a key={key++} href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      );
    } else if (kind === "kisi") {
      nodes.push(<Link key={key++} to={`/kisi/${tail}`}>{refLabel}</Link>);
    } else if (kind === "kulup") {
      nodes.push(<Link key={key++} to={`/kulup/${tail}`}>{refLabel}</Link>);
    } else if (kind === "takim") {
      nodes.push(<Link key={key++} to={`/takim/${tail}`}>{refLabel}</Link>);
    } else if (kind === "baslik") {
      nodes.push(<Link key={key++} to={`/gundem/${tail}`}>{refLabel}</Link>);
    } else {
      // "mac" — no cheap URL from the id alone, show the readable label only
      nodes.push(refLabel);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < label.length) {
    nodes.push(label.slice(lastIndex));
  }
  return nodes;
};

export const AdminAuditLogs: React.FC<AdminAuditLogsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  const [logs, setLogs]             = useState<AuditLogEntry[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Filter state
  const [actorSearch, setActorSearch]   = useState("");
  const [action, setAction]             = useState<AuditAction | "">("");
  const [targetType, setTargetType]     = useState<AuditTargetType | "">("");
  const [startDate, setStartDate]       = useState("");
  const [endDate, setEndDate]           = useState("");

  // Detail modal
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const filters: AuditLogFilters = {
        actorSearch: actorSearch || undefined,
        action:      action      || undefined,
        targetType:  targetType  || undefined,
        // Convert date-only string to ISO datetime for the backend
        startDate: startDate ? startDate + "T00:00:00" : undefined,
        endDate:   endDate   ? endDate   + "T23:59:59" : undefined,
      };
      const res = await getAuditLogs(page, ADMIN_PAGE_SIZE, filters);
      setLogs(res.content);
      setCurrentPage(res.currentPage);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch {
      setError(t("audit_load_error"));
    } finally {
      setLoading(false);
    }
  }, [actorSearch, action, targetType, startDate, endDate, t]);

  // Reload from page 0 whenever filters change
  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const handleClearFilters = () => {
    setActorSearch("");
    setAction("");
    setTargetType("");
    setStartDate("");
    setEndDate("");
  };

  const hasFilters = actorSearch || action || targetType || startDate || endDate;

  return (
    <div className="admin-audit">

      {/* ── Toolbar ── */}
      <div className="admin-audit__toolbar">
        <div className="admin-audit__filters">

          {/* Actor username search */}
          <input
            className="admin-audit__filter-input"
            type="text"
            placeholder={t("audit_filter_actor")}
            value={actorSearch}
            onChange={e => setActorSearch(e.target.value)}
          />

          {/* Action filter */}
          <select
            className="admin-audit__filter-select"
            value={action}
            onChange={e => setAction(e.target.value as AuditAction | "")}
          >
            <option value="">{t("audit_filter_action_all")}</option>
            {ACTION_OPTIONS.map(a => (
              <option key={a} value={a}>{t("audit_action_" + a)}</option>
            ))}
          </select>

          {/* Target type filter */}
          <select
            className="admin-audit__filter-select"
            value={targetType}
            onChange={e => setTargetType(e.target.value as AuditTargetType | "")}
          >
            <option value="">{t("audit_filter_target_all")}</option>
            {TARGET_OPTIONS.map(tt => (
              <option key={tt} value={tt}>{t("audit_target_" + tt)}</option>
            ))}
          </select>

          {/* Date range */}
          <input
            className="admin-audit__filter-date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            title={t("audit_filter_start_date")}
          />
          <input
            className="admin-audit__filter-date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            title={t("audit_filter_end_date")}
          />

          {hasFilters && (
            <button className="admin-audit__clear-btn" onClick={handleClearFilters}>
              {t("audit_clear_filters")}
            </button>
          )}
        </div>

        {!loading && (
          <span className="admin-audit__count">
            {t("audit_total_count", { count: totalItems })}
          </span>
        )}
      </div>

      {/* ── Error ── */}
      {error && <p className="admin-audit__empty">{error}</p>}

      {/* ── Table ── */}
      {loading ? (
        <p className="admin-audit__empty">{t("loading")}</p>
      ) : logs.length === 0 ? (
        <p className="admin-audit__empty">{t("audit_empty")}</p>
      ) : (
        <div className="admin-audit__table-wrapper">
          <table className="admin-audit__table">
            <thead>
              <tr>
                <th>{t("audit_col_date")}</th>
                <th>{t("audit_col_actor")}</th>
                <th>{t("audit_col_action")}</th>
                <th>{t("audit_col_target_type")}</th>
                <th>{t("audit_col_target")}</th>
                <th>{t("audit_col_detail")}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} onClick={() => setSelectedLog(log)}>

                  {/* Date */}
                  <td>
                    <span className="audit-date">{formatAdminDate(log.createdAt)}</span>
                  </td>

                  {/* Actor */}
                  <td>
                    <span className="audit-actor-link">{log.actorUsername}</span>
                  </td>

                  {/* Action badge */}
                  <td>
                    <span className={`audit-action-badge audit-action-badge--${actionToCssClass(log.action)}`}>
                      {t("audit_action_" + log.action)}
                    </span>
                  </td>

                  {/* Target type chip */}
                  <td>
                    <span className={`audit-target-chip audit-target-chip--${log.targetType.toLowerCase()}`}>
                      {t("audit_target_" + log.targetType)}
                    </span>
                  </td>

                  {/* Target label — sport type strings inside team labels are translated */}
                  <td>
                    <span className="audit-target-label" title={log.targetLabel ?? ""}>
                      {log.targetLabel ? translateSportTypes(log.targetLabel, t) : "—"}
                    </span>
                  </td>

                  {/* Detail */}
                  <td>
                    <span className="audit-detail" title={log.detail ?? ""}>
                      {log.detail ? formatAuditDetail(log.detail, t) : "—"}
                    </span>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={(page: number) => loadPage(page - 1)}
        />
      )}

      {/* ── Detail modal — portal into document.body to escape all z-index stacking contexts ── */}
      {selectedLog && ReactDOM.createPortal(
        <div
          className="audit-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedLog(null); }}
        >
          <div
            className="audit-modal"
          >
            <div className="audit-modal__header">
              <h3 className="audit-modal__title">{t("audit_detail_title")}</h3>
              <button
                className="audit-modal__close"
                onClick={() => setSelectedLog(null)}
              >
                ✕
              </button>
            </div>

            <div className="audit-modal__row">
              <span className="audit-modal__label">{t("audit_col_date")}</span>
              <span className="audit-modal__value">{formatAdminDate(selectedLog.createdAt)}</span>
            </div>
            <div className="audit-modal__row">
              <span className="audit-modal__label">{t("audit_col_actor")}</span>
              <span className="audit-modal__value">{selectedLog.actorUsername} (ID: {selectedLog.actorId})</span>
            </div>
            <div className="audit-modal__row">
              <span className="audit-modal__label">{t("audit_col_action")}</span>
              <span className="audit-modal__value">
                <span className={`audit-action-badge audit-action-badge--${actionToCssClass(selectedLog.action)}`}>
                  {t("audit_action_" + selectedLog.action)}
                </span>
              </span>
            </div>
            <div className="audit-modal__row">
              <span className="audit-modal__label">{t("audit_col_target_type")}</span>
              <span className="audit-modal__value">
                <span className={`audit-target-chip audit-target-chip--${selectedLog.targetType.toLowerCase()}`}>
                  {t("audit_target_" + selectedLog.targetType)}
                </span>
              </span>
            </div>
            {selectedLog.targetLabel && (
              <div className="audit-modal__row">
                <span className="audit-modal__label">{t("audit_col_target")}</span>
                {/* Full stored text, wrapping over multiple lines. USER targets
                    link to the profile; entry snippets get their ref tokens and
                    URLs rendered as links; sport enums are translated. */}
                <span className="audit-modal__value audit-modal__value--target">
                  {selectedLog.targetType === "USER" && selectedLog.targetId !== null ? (
                    <Link to={generateProfileUrl(selectedLog.targetLabel, selectedLog.targetId)}>
                      {selectedLog.targetLabel}
                    </Link>
                  ) : (
                    linkifyAuditLabel(translateSportTypes(selectedLog.targetLabel, t))
                  )}
                </span>
              </div>
            )}
            {selectedLog.targetId !== null && (
              <div className="audit-modal__row">
                <span className="audit-modal__label">{t("audit_target_id")}</span>
                <span className="audit-modal__value">{selectedLog.targetId}</span>
              </div>
            )}
            {selectedLog.detail && (
              <div className="audit-modal__row">
                <span className="audit-modal__label">{t("audit_col_detail")}</span>
                <span className="audit-modal__value">{formatAuditDetail(selectedLog.detail, t)}</span>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};